import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { BiCheck } from 'react-icons/bi';
import { IoClose } from 'react-icons/io5';
import * as yup from 'yup';
import { AiOutlineSearch } from 'react-icons/ai';

import { useOnClickOutside } from 'modules/common/hooks';
import { Text, Button } from 'modules/common';

export const matches = {
  '0-9': () => ({
    regex: /[0-9]/,
    message: 'Password requires a number',
  }),
  'a-z': () => ({
    regex: /[a-z]/,
    message: 'Password requires a lowercase letter',
  }),
  'A-Z': () => ({
    regex: /[A-Z]/,
    message: 'Password requires an uppercase letter',
  }),
  '^\\w': () => ({
    regex: /[^\w]/,
    message: 'Password requires a symbol',
  }),
};
export const strengthValidation = (v) => {
  const $matched = Object.entries(matches);

  let passed = {};

  $matched?.map((item) => {
    const label = item?.[0];
    const value = item?.[1]?.();

    if (value?.regex?.test(v) && v?.length > 0) {
      passed = {
        ...passed,
        [label]: value?.message,
      };
    }

    return item;
  });

  return passed;
};
export const passedYupValidation = ({
  key = 'password',
  passAtLeast = 3,
  isRequired = true,
} = {}) =>
  yup.string().test({
    test: (v, { parent, createError }) => {
      const value = parent?.[key] || '';

      if (!isRequired && value?.length <= 0 && !value) return true;

      if (Object.keys(strengthValidation(value) || {})?.length >= passAtLeast) {
        return true;
      }

      return createError({
        message: `Pass at least ${passAtLeast} requirements to proceed. ▼`,
      });
    },
  });
export const passwordYupValidation = ({
  len = 8,
  minMessage = `Password must be atleast ${len} characters`,
  requiredMessage = 'Password is required',
  isRequired = true,
} = {}) => {
  let $yup = yup.string().min(len, minMessage);

  if (isRequired) $yup = $yup.required(requiredMessage);

  return $yup;
};

const FormInput = React.forwardRef(
  (
    {
      type,
      name,
      label,
      trailingItem,
      error,
      leadingItem,
      onChange,
      required,
      className,
      labelClassName,
      onSetFieldValue,
      containerClassName,
      errorClassName,
      withStrengthValidation,
      passAtLeast,
      isOptional,
      helperText,
      disabled,
      wrapperClassName,
      leadingType,
      trailingType,
      buttonProps,
      value,
      ...rest
    },
    ref
  ) => {
    const itemProps = {
      buttonProps: {
        disabled,
        size: 'xs',
      },
      iconProps: {
        className: 'h-4 w-4',
      },
    };

    const [showPassword, setShowPassword] = React.useState(false);
    const dropdownRef = React.useRef();
    const [showStrengthValidation, setShowStrengthValidation] =
      React.useState(false);
    const errorPassed = withStrengthValidation ? strengthValidation(value) : {};

    const handleOnFocus = () => {
      setShowStrengthValidation(true);

      try {
        ref.current.focus();
      } catch (err) {
        // do_nothing
      }
    };

    useOnClickOutside(dropdownRef, setShowStrengthValidation);

    const handleOnChange = (e) => {
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, e?.target?.value);
        return;
      }
      if (typeof onChange === 'function')
        onChange((prev) => ({
          ...prev,
          [name]: e?.target.value,
        }));
    };

    const handleOnShowPassword = () => {
      setShowPassword(!showPassword);
    };

    const dispatchItem = {
      password: trailingType === 'password' && type === 'password' && (
        <span onClick={handleOnShowPassword} role="presentation">
          {showPassword ? (
            <HiEyeOff className="h-4 w-4" />
          ) : (
            <HiEye className="h-4 w-4" />
          )}
        </span>
      ),
      search: <AiOutlineSearch className="h-4 w-4" />,
      button: (
        <div className="my-[-5px] -mx-ds-4">
          <Button size="xs" {...buttonProps} />
        </div>
      ),
      'default-button': (
        <Button
          {...buttonProps}
          className={cn(
            '-m-ds-12',
            trailingType === 'default-button'
              ? 'border-t-0 border-r-0 border-b-0 !ml-0'
              : 'border-t-0 border-l-0 border-b-0 !mr-0',
            buttonProps?.className
          )}
        />
      ),
    };

    return (
      <div className={cn('w-full', wrapperClassName)}>
        <div className="w-full relative" ref={dropdownRef}>
          {withStrengthValidation && showStrengthValidation && (
            <div className="absolute top-20 left-0 z-10 arrow-top">
              <div className="mt-2.5 text-xs space-y-2 card p-4">
                <div>
                  {Object.entries(matches)?.map((item) => (
                    <div className="flex items-center gap-2" key={item?.[0]}>
                      <div>
                        {Object.keys(errorPassed)?.includes(item?.[0]) ? (
                          <BiCheck className="h-4 w-4 text-utility-success-base" />
                        ) : (
                          <IoClose className="h-4 w-4 text-utility-danger-base" />
                        )}
                      </div>
                      <div>{item?.[1]?.()?.message}.</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div
            className={cn(
              'form-container transition duration-300 ease-in-out',
              containerClassName,
              {
                'form-container': !error,
                'form-container-error': error,
              }
            )}
            onClick={handleOnFocus}
            role="presentation"
          >
            {label && (
              <label
                className={cn(
                  'ds-form-label flex items-center',
                  labelClassName
                )}
                onClick={handleOnFocus}
                role="presentation"
              >
                <div className="flex-1">
                  {label}{' '}
                  {required ? (
                    <span className="text-utility-danger-base">*</span>
                  ) : (
                    ''
                  )}
                </div>

                {isOptional && <div className="font-normal">Optional</div>}
              </label>
            )}

            <div
              className={cn(
                'transition duration-300 ease-in-out relative gap-[10px]',
                {
                  'ds-form-input': !error,
                  'ds-form-input-error': error,
                  'ds-form-input-disabled': disabled,
                }
              )}
            >
              {Boolean(leadingItem || dispatchItem?.[leadingType]) && (
                <div
                  className={cn(
                    'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                    {
                      'form-icon': !disabled,
                      'form-icon-disabled': disabled,
                    }
                  )}
                >
                  {leadingItem ? (
                    <>
                      {typeof leadingItem === 'function'
                        ? leadingItem(itemProps)
                        : leadingItem}
                    </>
                  ) : (
                    dispatchItem?.[leadingType]
                  )}
                </div>
              )}

              <input
                id={rest?.id ?? name}
                type={
                  trailingType === 'password'
                    ? `${showPassword ? 'text' : 'password'}`
                    : type
                }
                name={name}
                autoComplete="off"
                onChange={handleOnChange}
                className={cn(
                  'w-full flex items-center disabled:cursor-not-allowed h-[16px] transition-all duration-300 ease-in-out',
                  !disabled
                    ? 'placeholder-placeholder'
                    : 'placeholder-disabled',
                  className,
                  { 'bg-utility-danger-muted': error }
                )}
                required={required}
                disabled={disabled}
                value={value}
                {...rest}
                style={{ outline: 'none', ...rest?.style }}
                ref={ref}
              />

              {Boolean(trailingItem || dispatchItem?.[trailingType]) && (
                <div
                  className={cn(
                    'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                    {
                      'form-icon': !disabled,
                      'form-icon-disabled': disabled,
                    }
                  )}
                >
                  {trailingItem ? (
                    <>
                      {typeof trailingItem === 'function'
                        ? trailingItem(itemProps)
                        : trailingItem}
                    </>
                  ) : (
                    dispatchItem?.[trailingType]
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {(Boolean(error) || Boolean(helperText)) && (
          <Text
            type="caption"
            className={cn(
              'mt-ds-6',
              {
                'ds-form-helper': !error,
                'ds-form-helper-error': error,
                'ds-form-helper-disabled': disabled,
              },
              errorClassName
            )}
          >
            <span>{error || helperText || ''}</span>
            <span className="form-error-badge" />
          </Text>
        )}
      </div>
    );
  }
);

FormInput.defaultProps = {
  label: 'Name',
  containerClassName: '',
  labelClassName: '',
  errorClassName: '',
  className: '',
  required: false,
  type: 'text',
  error: false,
  trailingItem: false,
  onSetFieldValue: false,
  onChange: false,
  leadingItem: false,
  withStrengthValidation: false,
  passAtLeast: 3,
  isOptional: false,
  helperText: '',
  disabled: false,
  wrapperClassName: '',
  leadingType: '',
  trailingType: '',
  value: '',
  buttonProps: {},
};

FormInput.propTypes = {
  leadingType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['password', 'search', 'button']),
  ]),
  trailingType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['password', 'search', 'button']),
  ]),
  buttonProps: PropTypes.instanceOf(Object),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  wrapperClassName: PropTypes.string,
  value: PropTypes.string,
  isOptional: PropTypes.bool,
  helperText: PropTypes.string,
  passAtLeast: PropTypes.number,
  disabled: PropTypes.bool,
  withStrengthValidation: PropTypes.bool,
  name: PropTypes.string.isRequired,
  errorClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  trailingItem: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Object)]),
};

export default React.memo(FormInput);
